import {
  init,
  RematchRootState,
  RematchDispatch,
  ModelEffects,
  ModelConfig
} from '@rematch/core';
import createLoadingPlugin from '@rematch/loading';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import * as models from './models';

const loadingPlugin = createLoadingPlugin({});

export type ExtractRematchLoadingFromEffects<
  effects extends ModelConfig<any, any, M>['effects'],
  M
> = effects extends (...args: any[]) => infer R
  ? R extends ModelEffects<any>
    ? ExtractRematchLoadingFromEffectsObject<R>
    : Record<string, unknown>
  : effects extends ModelEffects<any>
  ? ExtractRematchLoadingFromEffectsObject<effects>
  : Record<string, unknown>;

export type ExtractRematchLoadingFromEffectsObject<
  effects extends ModelEffects<any>
> = { [effectKey in keyof effects]: boolean };

interface LoadingState<M> {
  loading: {
    global: boolean;
    models: { [k in keyof M]: boolean };
    effects: {
      [k in keyof M]: M[k] extends ModelConfig<any, any, M>
        ? ExtractRematchLoadingFromEffects<M[k]['effects'], M>
        : never;
    };
  };
}

export const store = init({
  models: {
    ...models
  },
  plugins: [loadingPlugin]
});

export type Store = typeof store;
export type RootModel = typeof models;
export type Dispatch = RematchDispatch<RootModel>;
export type RootState = RematchRootState<RootModel> & LoadingState<RootModel>;

export const useRematchState = <S extends RootState, MS>(
  state: (state: S) => MS
) => {
  const mapState = useSelector(state, shallowEqual);
  return mapState;
};

export const useRematchDispatch = <D extends Dispatch, MD>(
  selector: (dispatch: D) => MD
) => {
  const dispatch = useDispatch<D>();
  return selector(dispatch);
};

// example
// const { load } = useRematchState((dispatch: RootDispatch) => ({
//   load: state.users.load
// }))
// const { load } = useRematchDispatch((dispatch: RootDispatch) => ({
//   load: dispatch.users.load
// }))
